// ============================================================
// Settings Page
// ============================================================

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { Save, Users, Plus, Trash2, X, UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';

export function SettingsPage() {
    const { tenant, user } = useAuthStore();
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState('general');
    const [showInvite, setShowInvite] = useState(false);

    const { data: tenantData } = useQuery({
        queryKey: ['tenant'],
        queryFn: () => api.get('/tenant'),
    });

    const { data: membersData } = useQuery({
        queryKey: ['members'],
        queryFn: () => api.get('/tenant/members'),
    });

    const t = tenantData?.data || tenant;
    const members = membersData?.data || [];

    const removeMutation = useMutation({
        mutationFn: (membershipId: string) => api.delete(`/tenant/members/${membershipId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['members'] });
            toast.success('Member removed');
        },
        onError: (err: any) => toast.error(err.message),
    });

    return (
        <div className="animate-fadeIn">
            <div className="page-header">
                <div>
                    <h1>Settings</h1>
                    <p className="subtitle">Manage your workspace and team</p>
                </div>
            </div>

            <div className="tabs">
                <button className={`tab ${activeTab === 'general' ? 'active' : ''}`} onClick={() => setActiveTab('general')}>General</button>
                <button className={`tab ${activeTab === 'team' ? 'active' : ''}`} onClick={() => setActiveTab('team')}>
                    <Users size={16} style={{ marginRight: 4 }} /> Team Members
                </button>
            </div>

            {activeTab === 'general' && (
                <div className="card" style={{ maxWidth: 600 }}>
                    <h3 style={{ marginBottom: 20, fontWeight: 700 }}>Workspace Settings</h3>
                    <div className="form-group">
                        <label className="form-label">Company Name</label>
                        <input className="form-input" defaultValue={t?.name || ''} />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Slug</label>
                        <input className="form-input" defaultValue={t?.slug || ''} disabled style={{ opacity: 0.5 }} />
                    </div>
                    <button className="btn btn-primary mt-4" onClick={() => toast.success('Settings saved')}>
                        <Save size={16} /> Save Changes
                    </button>
                </div>
            )}

            {activeTab === 'team' && (
                <div className="card">
                    <div className="card-header">
                        <h3 style={{ fontWeight: 700 }}>Team Members</h3>
                        <button className="btn btn-primary btn-sm" onClick={() => setShowInvite(true)}>
                            <Plus size={16} /> Invite Member
                        </button>
                    </div>
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {members.map((m: any) => (
                                <tr key={m.membershipId || m.userId || m.id}>
                                    <td style={{ fontWeight: 600 }}>{m.name || m.user?.name}</td>
                                    <td style={{ color: '#4b5563' }}>{m.email || m.user?.email}</td>
                                    <td>
                                        <span className={`badge badge-${m.role === 'owner' ? 'accent' : m.role === 'admin' ? 'info' : 'success'}`}>
                                            {m.role}
                                        </span>
                                    </td>
                                    <td>
                                        {m.role !== 'owner' && (
                                            <button className="btn btn-ghost btn-sm"
                                                onClick={() => {
                                                    if (confirm('Remove this member?')) {
                                                        removeMutation.mutate(m.membershipId || m.id);
                                                    }
                                                }}>
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* Invite Member Modal */}
                    {showInvite && <InviteMemberModal onClose={() => setShowInvite(false)} />}
                </div>
            )}
        </div>
    );
}

function InviteMemberModal({ onClose }: { onClose: () => void }) {
    const queryClient = useQueryClient();
    const [form, setForm] = useState({ email: '', name: '', password: '', role: 'recruiter' });

    const inviteMutation = useMutation({
        mutationFn: (data: typeof form) => api.post('/tenant/members', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['members'] });
            toast.success('Member invited successfully!');
            onClose();
        },
        onError: (err: any) => toast.error(err.message || 'Failed to invite member'),
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.email || !form.name || !form.password) {
            toast.error('Please fill all fields');
            return;
        }
        inviteMutation.mutate(form);
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={onClose}>
            <div className="card" style={{
                width: 480, maxHeight: '90vh', overflow: 'auto', position: 'relative',
            }} onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h3 style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <UserPlus size={20} color="#6366f1" /> Invite Team Member
                    </h3>
                    <button className="btn btn-ghost btn-sm" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label className="form-label">Full Name *</label>
                        <input className="form-input" value={form.name}
                            onChange={e => setForm({ ...form, name: e.target.value })}
                            placeholder="John Doe" />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Email Address *</label>
                        <input className="form-input" type="email" value={form.email}
                            onChange={e => setForm({ ...form, email: e.target.value })}
                            placeholder="john@company.com" />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Temporary Password *</label>
                        <input className="form-input" type="password" value={form.password}
                            onChange={e => setForm({ ...form, password: e.target.value })}
                            placeholder="Set an initial password" />
                        <span style={{ fontSize: 11, color: '#6b7280' }}>
                            Member should change this after first login
                        </span>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Role</label>
                        <select className="form-select" value={form.role}
                            onChange={e => setForm({ ...form, role: e.target.value })}>
                            <option value="viewer">Viewer — Read-only access</option>
                            <option value="hiring_manager">Hiring Manager — Score overrides</option>
                            <option value="recruiter">Recruiter — Manage jobs & assessments</option>
                            <option value="admin">Admin — Full access except billing</option>
                        </select>
                    </div>

                    <div className="flex gap-3 justify-end mt-6">
                        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
                        <button type="submit" className="btn btn-primary" disabled={inviteMutation.isPending}>
                            <UserPlus size={16} /> {inviteMutation.isPending ? 'Inviting...' : 'Invite Member'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
